import { findLast, last } from 'lodash';

import {
  Cell,
  ChatCell,
  ChatExploration,
  ChatMessage,
  ChatUserMessage,
  ChatUserQuestion,
  ConversationCell,
  ConversationPart,
  Exploration,
} from '@/explore/types';

import { extractCellFromExploration } from '@/explore/exploration/utils';

import { isChatCell, updateChatCellMessage } from './chat-cell';

export const isConversationCell = (cell: Cell): cell is ConversationCell =>
  cell.kind === 'conversation';

export const isConversationPart = (cell: Cell): cell is ConversationPart =>
  'conversationId' in cell;

export const belongsToConversation = (cell: Cell, conversationId: string) =>
  isConversationPart(cell) && cell.conversationId === conversationId;

export function getConversationCells(cells: Cell[], conversationId: string): ConversationPart[] {
  // Double filtering is required here to satisfy types
  return cells.filter(isConversationPart).filter((cell) => cell.conversationId === conversationId);
}

export function isExplorationMessage(
  message: ChatMessage | ChatExploration,
): message is ChatExploration {
  return message.type === 'exploration';
}

export const isUserMessage = (msg: ChatMessage | ChatExploration): msg is ChatUserMessage =>
  msg.role === 'user';

export const isUserQuestionMessage = (
  msg: ChatMessage | ChatExploration,
): msg is ChatUserQuestion => msg.role === 'user' && msg.type !== 'clarifying_answer';

export function convertConversationCellsToMessages(cells: ConversationPart[]) {
  // existing cells with new messages are added to the end of the cells array
  // track processed message ids to avoid duplicates
  const processedMessageIds = new Set<string>();

  // Remove leading exploration cells (when conversation = ask about this block)
  const slicedCells = cells.slice(cells.findIndex((cell) => isChatCell(cell)));

  return slicedCells.reduce<(ChatMessage | ChatExploration)[]>((acc, cell) => {
    if (!isChatCell(cell)) {
      // Wrap cell in exploration or merge with previous
      const previousMessage = last(acc);
      const previousExplorationMessage =
        previousMessage !== undefined && isExplorationMessage(previousMessage)
          ? previousMessage
          : undefined;
      return previousExplorationMessage !== undefined
        ? [
            ...acc.slice(0, -1),
            {
              ...previousExplorationMessage,
              exploration: {
                ...previousExplorationMessage.exploration,
                view: { cells: [...previousExplorationMessage.exploration.view.cells, cell] },
              },
            },
          ]
        : [
            ...acc,
            {
              id: cell.id,
              role: 'assistant' as const,
              type: 'exploration' as const,
              exploration: {
                explorationId: cell.conversationId,
                name: 'Exploration',
                labels: {},
                parameters: [],
                view: { cells: [cell] },
              },
            },
          ];
    }
    return [
      ...acc,
      ...cell.messages
        .filter(({ id }) => !processedMessageIds.has(id))
        .map((message) => {
          processedMessageIds.add(message.id);
          if (message.role === 'assistant') {
            return {
              id: message.id,
              role: 'assistant' as const,
              type: 'clarifying_question' as const,
              message: message.message ?? '',
            };
          }
          if (acc.length === 0 && message.type === 'followup_question') {
            // User requested an edit from an existing cell
            return {
              id: message.id,
              role: 'user' as const,
              type: 'initial_user_prompt' as const,
              sources: message.sources ?? [],
              message: message.message,
              research: message.research ?? false,
            };
          }
          if (isUserQuestionMessage(message)) {
            return {
              id: message.id,
              role: 'user' as const,
              type: message.type,
              message: message.message ?? '',
              sources: message.sources ?? [],
              research: message.research ?? false,
            };
          }

          return {
            id: message.id,
            role: 'user' as const,
            type: message.type,
            message: message.message ?? '',
          };
        }),
    ];
  }, []);
}

export function getConversationSelectors(conversationId: string, cells: Cell[]) {
  const referencedCell = cells.find(
    (cell) =>
      belongsToConversation(cell, conversationId) && !isChatCell(cell) && !isConversationCell(cell),
  );
  return referencedCell !== undefined ? [`view.cells[?(@.id == "${referencedCell.id}")]`] : [];
}

export function addConversationId(cell: Cell, conversationId: string) {
  if (isConversationCell(cell)) {
    return cell;
  }

  return {
    ...cell,
    conversationId,
  };
}

export function removeConversationId(cell: Cell): Cell {
  if (!isConversationPart(cell) || isChatCell(cell) || isConversationCell(cell)) {
    return cell;
  }

  const { conversationId: _, ...rest } = cell;
  return rest;
}

export const updateConversationMessage = (
  conversationId: string,
  cells: Cell[],
  message: ChatUserMessage,
) => {
  return cells.map((cell) => {
    if (
      isChatCell(cell) &&
      cell.conversationId === conversationId &&
      cell.messages.some(({ id }) => id === message.id)
    ) {
      return updateChatCellMessage(cell, message);
    }

    return cell;
  });
};

export const discardConversationAfterMessage = (
  conversationId: string,
  cells: Cell[],
  messageId: string,
): Cell[] => {
  const messageCellIdx = cells.findIndex(
    (cell) => isChatCell(cell) && cell.messages.some((m) => m.id === messageId),
  );

  if (messageCellIdx === -1) {
    return cells;
  }

  return cells
    .map((cell, cellIdx) => {
      if (cellIdx === messageCellIdx && isChatCell(cell)) {
        const messageIdx = cell.messages.findIndex(({ id }) => id === messageId);

        if (messageIdx === -1) {
          return cell;
        }

        return {
          ...cell,
          messages: cell.messages.slice(0, messageIdx + 1),
        };
      }

      return cell;
    })
    .filter((cell, index) => {
      if (!belongsToConversation(cell, conversationId)) {
        return true;
      }

      return index <= messageCellIdx;
    });
};

export const removeConversationCell = (
  conversationId: string,
  cellId: string,
  cells: Cell[],
): Cell[] => {
  const discardedCellIdx = cells.findIndex(({ id }) => id === cellId);
  const prevConversationCell = cells.at(discardedCellIdx - 1);
  const nextConversationCell = cells.at(discardedCellIdx + 1);
  if (
    prevConversationCell !== undefined &&
    belongsToConversation(prevConversationCell, conversationId) &&
    isChatCell(prevConversationCell) &&
    nextConversationCell !== undefined &&
    belongsToConversation(nextConversationCell, conversationId) &&
    isChatCell(nextConversationCell)
  ) {
    // If between two chat cells, remove the following conversation cells
    return cells.reduce<Cell[]>((result, cell, cellIdx) => {
      if (belongsToConversation(cell, conversationId) && cellIdx >= discardedCellIdx) {
        return result;
      }
      result.push(cell);
      return result;
    }, []);
  }
  return cells.filter(({ id }) => id !== cellId);
};

export const acceptConversationAfterReply = (
  conversationId: string,
  exploration: Exploration,
  cutoffIndex: number,
) => {
  const conversationCells = getConversationCells(exploration.view.cells, conversationId);
  const [firstCell] = conversationCells;
  const originalCell = !isChatCell(firstCell) ? firstCell : undefined;
  const cellsToKeep = conversationCells
    .filter(
      (c, index) =>
        (!isChatCell(c) && (cutoffIndex === null || index > cutoffIndex)) ||
        c.id === originalCell?.id,
    )
    .map((cell) => {
      const { cell: extractedCell, dependencies } = extractCellFromExploration(
        cell.id,
        exploration,
      );
      return [extractedCell, ...dependencies];
    })
    .flat();

  return exploration.view.cells.reduce<Cell[]>((result, c) => {
    if (!isConversationPart(c) || !belongsToConversation(c, conversationId)) {
      result.push(c);
      return result;
    }

    const extractedCell = cellsToKeep.find((cell) => cell.id === c.id);
    if (extractedCell !== undefined) {
      result.push(removeConversationId(extractedCell));
    }

    return result;
  }, []);
};

export const isStartOfConversation = (cell: ConversationPart, cells: Cell[]) => {
  const conversationStart = cells.find((c) => belongsToConversation(c, cell.conversationId));
  return conversationStart?.id === cell.id;
};

export const isEndOfConversation = (cell: ConversationPart, cells: Cell[]) => {
  const lastCell = findLast(cells, (c) => belongsToConversation(c, cell.conversationId));
  return lastCell?.id === cell.id;
};

export const normalizeConversationBlocks = (cells: Cell[]) => {
  let chatCell: ChatCell | null = null;
  cells = cells.reduce<Cell[]>((acc, c) => {
    if (isChatCell(c)) {
      chatCell = c;
      return acc;
    } else if (chatCell !== null) {
      acc.push(chatCell);
      chatCell = null;
    }
    return [...acc, c];
  }, []);
  return [...cells, ...(chatCell !== null ? [chatCell] : [])];
};

export const appendConversationCells = (
  cells: Cell[],
  cellsToAdd: ConversationPart[],
  conversationId: string,
) => {
  const lastConversationCellIdx = cells.findLastIndex(
    (c) => isConversationPart(c) && c.conversationId === conversationId,
  );
  return cells.reduce<Cell[]>((result, c, idx) => {
    if (idx === lastConversationCellIdx) {
      result.push(c, ...cellsToAdd);
      return result;
    }

    result.push(c);
    return result;
  }, []);
};

export const isStartOfReply = (cellToCheck: Cell, cells: Cell[]) => {
  if (!isConversationPart(cellToCheck) || isChatCell(cellToCheck)) {
    return false;
  }

  const conversationCells = getConversationCells(cells, cellToCheck.conversationId);
  const idx = conversationCells.findIndex((cell) => cell.id === cellToCheck.id);
  const previousCell = conversationCells.at(idx - 1);
  return idx === 0 || (previousCell !== undefined && isChatCell(previousCell));
};

export const isEndOfReply = (cellToCheck: Cell, cells: Cell[]) => {
  if (!isConversationPart(cellToCheck) || isChatCell(cellToCheck)) {
    return false;
  }

  const conversationCells = getConversationCells(cells, cellToCheck.conversationId);
  const idx = conversationCells.findIndex((cell) => cell.id === cellToCheck.id);
  const followingCell = conversationCells.at(idx + 1);
  return (
    followingCell !== undefined && (isChatCell(followingCell) || isConversationCell(followingCell))
  );
};

/**
 * Checks if a conversation requires a follow-up chat cell to be added at the end.
 * A follow-up is required if the last cell in the conversation is not a chat cell.
 */
export const requiresFollowupChatCell = (
  cells: Cell[],
  conversationId: string,
  currentCellId?: string,
  streamCells?: ConversationPart[],
): boolean => {
  const conversationCells = getConversationCells(cells, conversationId);
  const lastStreamCell = streamCells?.at(-1);
  const lastCell = lastStreamCell ?? conversationCells.at(-1);

  return lastCell?.id !== currentCellId && lastCell !== undefined && !isChatCell(lastCell);
};
